import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Radio, ExternalLink, Play, Loader2, Search, FileText, CheckCircle2, XCircle, Clock,
  StopCircle, Image, ArrowUp, ArrowDown, Bookmark, BookmarkCheck,
  ArrowRightCircle,
} from 'lucide-react';

function errorAction(text: string) {
  return {
    label: 'Copy',
    onClick: (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      window.prompt('Copy this error:', text);
    },
  };
}
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { TendersTable } from '../components/tender/TendersTable';
import { SavedTendersTable } from '../components/tender/SavedTendersTable';
import { RejectedTendersTable } from '../components/tender/RejectedTendersTable';
import { KeywordsTable, type KeywordCategories } from '../components/tender/KeywordsTable';
import { ScoreBadge } from '../components/tender/ScoreBadge';
import { type TenderResult, type SavedTender, tenderKey } from '../components/tender/types';

// ── Types matching crawlers API ──

interface CrawlerLastRun {
  jobId: string;
  completedAt: string;
  resultCount: number;
  durationMs: number;
}

interface CrawlerStats {
  totalRuns: number;
  successfulRuns: number;
  failedRuns: number;
  totalResults: number;
  totalDurationMs: number;
  successRate: number;
  avgDurationMs: number;
  avgResultsPerRun: number;
}

interface CrawlerInfo {
  id: string;
  name: string;
  category: string;
  description: string;
  website: string;
  status: 'idle' | 'running';
  currentJobId?: string;
  lastRun?: CrawlerLastRun;
  stats?: CrawlerStats;
}

interface CrawlerJobDetail {
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  resultCount: number;
  durationMs?: number;
  error?: string;
}

interface TendersResponse {
  totalResults: number;
  sources: Record<string, { resultCount: number; cachedAt: string | null }>;
  results: TenderResult[];
}

interface JobResponse {
  id: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: string;
  completedAt?: string;
  crawlers: Record<string, CrawlerJobDetail>;
  resultCount: number;
  results: TenderResult[];
}

// ── API ──

const CRAWLERS_BASE = '/crawlers-api';

async function fetchTenderCrawlers(): Promise<CrawlerInfo[]> {
  const res = await fetch(`${CRAWLERS_BASE}/crawlers?category=tenders`);
  if (!res.ok) throw new Error(`Failed to fetch crawlers: ${res.status}`);
  const data = await res.json();
  return data.crawlers;
}

async function fetchTenders(): Promise<TendersResponse> {
  const res = await fetch(`${CRAWLERS_BASE}/tenders`);
  if (!res.ok) throw new Error(`Failed to fetch tenders: ${res.status}`);
  return res.json();
}

async function fetchKeywords(): Promise<KeywordCategories> {
  const res = await fetch(`${CRAWLERS_BASE}/tenders/keywords`);
  if (!res.ok) throw new Error(`Failed to fetch keywords: ${res.status}`);
  const data = await res.json();
  return data.categories;
}

async function saveKeywords(categories: KeywordCategories): Promise<void> {
  const res = await fetch(`${CRAWLERS_BASE}/tenders/keywords`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ categories }),
  });
  if (!res.ok) throw new Error(`Failed to save keywords: ${res.status}`);
}

// ── Saved Tenders API ──

async function fetchSavedTenders(): Promise<SavedTender[]> {
  const res = await fetch(`${CRAWLERS_BASE}/tenders/saved`);
  if (!res.ok) throw new Error(`Failed to fetch saved tenders: ${res.status}`);
  const data = await res.json();
  return data.saved;
}

async function apiSaveTender(tender: TenderResult, notes?: string): Promise<SavedTender> {
  const url = `${CRAWLERS_BASE}/tenders/saved`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tender, notes }),
  });
  if (!res.ok) throw new Error(`[POST ${url}] HTTP ${res.status} — is crawlers service running on port 3100?`);
  return res.json();
}

async function apiDeleteSavedTender(id: string): Promise<void> {
  const url = `${CRAWLERS_BASE}/tenders/saved/${id}`;
  const res = await fetch(url, { method: 'DELETE' });
  if (!res.ok) throw new Error(`[DELETE ${url}] HTTP ${res.status}`);
}

async function apiUpdateSavedNotes(id: string, notes: string): Promise<void> {
  const url = `${CRAWLERS_BASE}/tenders/saved/${id}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes }),
  });
  if (!res.ok) throw new Error(`[PATCH ${url}] HTTP ${res.status}`);
}

// ── Convert Tender to Opportunity ──

interface ConvertResult {
  opportunityId: string;
  companyId: string;
  companyName: string;
  title: string;
}

async function apiConvertToOpportunity(tenderId: string): Promise<ConvertResult> {
  // Try V4 API first (rest_server), fall back to crawlers-api
  const urls = [`/api/v4/tenders/${tenderId}/convert`, `${CRAWLERS_BASE}/tenders/${tenderId}/convert`];
  let lastError = '';
  for (const url of urls) {
    try {
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (res.ok) return res.json();
      const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      lastError = `[POST ${url}] ${err.error || `HTTP ${res.status}`}`;
    } catch {
      lastError = `[POST ${url}] Network error`;
    }
  }
  throw new Error(lastError);
}

// ── Excluded Tenders API ──

async function fetchExcludedKeys(): Promise<string[]> {
  const res = await fetch(`${CRAWLERS_BASE}/tenders/excluded`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.keys;
}

async function apiExcludeTender(key: string): Promise<void> {
  await fetch(`${CRAWLERS_BASE}/tenders/excluded`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  });
}

async function apiUnexcludeTender(key: string): Promise<void> {
  await fetch(`${CRAWLERS_BASE}/tenders/excluded`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  });
}

// ── Query keys ──

interface ScreenshotMeta {
  step: number;
  label: string;
  timestamp: string;
  pageUrl: string;
}

async function fetchCrawlerScreenshots(crawlerId: string): Promise<ScreenshotMeta[]> {
  const res = await fetch(`${CRAWLERS_BASE}/crawlers/${crawlerId}/screenshots`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.screenshots ?? [];
}

const crawlerKeys = {
  all: ['crawlers'] as const,
  tenders: () => [...crawlerKeys.all, 'tenders'] as const,
  tenderResults: () => [...crawlerKeys.all, 'tender-results'] as const,
  keywords: () => [...crawlerKeys.all, 'keywords'] as const,
  savedTenders: () => [...crawlerKeys.all, 'saved-tenders'] as const,
  excludedTenders: () => [...crawlerKeys.all, 'excluded-tenders'] as const,
  screenshots: (id: string) => [...crawlerKeys.all, 'screenshots', id] as const,
};

// ── Job run state ──

interface RunState {
  crawlerId: string;
  crawlerName: string;
  jobId: string | null;
  status: 'starting' | 'running' | 'completed' | 'failed' | 'cancelled';
  crawlerDetail?: CrawlerJobDetail;
  resultCount: number;
  results: TenderResult[];
}

// ── Step card for progress sheet ──

type StepStatus = 'pending' | 'running' | 'success' | 'failed';

function StepCard({
  icon: Icon,
  label,
  description,
  status,
  detail,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
  status: StepStatus;
  detail?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border p-5 flex gap-4 items-start transition-all duration-300',
        status === 'running' && 'border-blue-300 bg-blue-50',
        status === 'success' && 'border-green-300 bg-green-50',
        status === 'failed' && 'border-red-300 bg-red-50',
        status === 'pending' && 'border-muted bg-muted/20 opacity-50',
      )}
    >
      <div
        className={cn(
          'flex h-12 w-12 shrink-0 items-center justify-center rounded-full',
          status === 'running' && 'bg-blue-100',
          status === 'success' && 'bg-green-100',
          status === 'failed' && 'bg-red-100',
          status === 'pending' && 'bg-muted',
        )}
      >
        {status === 'running' ? (
          <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
        ) : status === 'success' ? (
          <CheckCircle2 className="h-6 w-6 text-green-600" />
        ) : status === 'failed' ? (
          <XCircle className="h-6 w-6 text-red-600" />
        ) : (
          <Icon className="h-6 w-6 text-muted-foreground" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{label}</span>
          {status === 'running' && <span className="text-xs text-blue-600 animate-pulse">Running...</span>}
          {status === 'success' && <span className="text-xs text-green-600">Done</span>}
          {status === 'failed' && <span className="text-xs text-red-600">Failed</span>}
          {status === 'pending' && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" /> Waiting
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        {detail && <p className="text-xs mt-1 text-green-700 font-medium">{detail}</p>}
      </div>
    </div>
  );
}

// ── Screenshot thumbnail ──

function crawlerScreenshotUrl(crawlerId: string, step = 0): string {
  return `${CRAWLERS_BASE}/crawlers/${crawlerId}/screenshots/${step}`;
}

function CrawlerScreenshotThumb({ crawlerId, onClick }: { crawlerId: string; onClick: () => void }) {
  const [hasError, setHasError] = useState(false);
  if (hasError) {
    return (
      <div className="w-[80px] h-[50px] rounded border bg-muted flex items-center justify-center">
        <Image className="h-4 w-4 text-muted-foreground/30" />
      </div>
    );
  }
  return (
    <img
      src={crawlerScreenshotUrl(crawlerId)}
      alt="Last run screenshot"
      className="w-[80px] h-[50px] object-cover rounded border cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all"
      loading="lazy"
      onClick={onClick}
      onError={() => setHasError(true)}
    />
  );
}

// ── Main page ──

export function TenderHunterPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('tenders');
  const [crawlerSearch, setCrawlerSearch] = useState('');
  const [selectedTender, setSelectedTender] = useState<TenderResult | null>(null);
  const [crawlerSortDesc, setCrawlerSortDesc] = useState(true);
  const [runState, setRunState] = useState<RunState | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [screenshotCrawler, setScreenshotCrawler] = useState<CrawlerInfo | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const crawlersQuery = useQuery({
    queryKey: crawlerKeys.tenders(),
    queryFn: fetchTenderCrawlers,
    staleTime: 30_000,
  });

  const tendersQuery = useQuery({
    queryKey: crawlerKeys.tenderResults(),
    queryFn: fetchTenders,
    staleTime: 60_000,
  });

  const keywordsQuery = useQuery({
    queryKey: crawlerKeys.keywords(),
    queryFn: fetchKeywords,
    staleTime: 5 * 60_000,
    enabled: tab === 'keywords',
  });

  const screenshotsQuery = useQuery({
    queryKey: crawlerKeys.screenshots(screenshotCrawler?.id ?? ''),
    queryFn: () => fetchCrawlerScreenshots(screenshotCrawler!.id),
    enabled: !!screenshotCrawler,
    staleTime: 30_000,
  });

  const savedTendersQuery = useQuery({
    queryKey: crawlerKeys.savedTenders(),
    queryFn: fetchSavedTenders,
    staleTime: 30_000,
  });

  const excludedKeysQuery = useQuery({
    queryKey: crawlerKeys.excludedTenders(),
    queryFn: fetchExcludedKeys,
    staleTime: 30_000,
  });

  const saveTenderMutation = useMutation({
    mutationFn: ({ tender, notes }: { tender: TenderResult; notes?: string }) => apiSaveTender(tender, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crawlerKeys.savedTenders() });
      toast.success('Tender saved');
    },
    onError: (err) => toast.error('Failed to save tender', { description: err.message, action: errorAction(err.message) }),
  });

  const deleteSavedMutation = useMutation({
    mutationFn: apiDeleteSavedTender,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crawlerKeys.savedTenders() });
      toast.success('Saved tender removed');
    },
    onError: (err) => toast.error('Failed to remove', { description: err.message, action: errorAction(err.message) }),
  });

  const updateNotesMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) => apiUpdateSavedNotes(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crawlerKeys.savedTenders() });
      toast.success('Notes updated');
    },
    onError: (err) => toast.error('Failed to update notes', { description: err.message, action: errorAction(err.message) }),
  });

  const [convertedTenders, setConvertedTenders] = useState<Map<string, { opportunityId: string; companyName: string }>>(new Map());

  const convertMutation = useMutation({
    mutationFn: apiConvertToOpportunity,
    onSuccess: (data, tenderId) => {
      queryClient.invalidateQueries({ queryKey: crawlerKeys.savedTenders() });
      setConvertedTenders((prev) => new Map(prev).set(tenderId, { opportunityId: data.opportunityId, companyName: data.companyName }));
      toast.success('Converted to opportunity', { description: `${data.title} → ${data.companyName}` });
    },
    onError: (err) => toast.error('Failed to convert', { description: err.message, action: errorAction(err.message) }),
  });

  const excludeMutation = useMutation({
    mutationFn: apiExcludeTender,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crawlerKeys.excludedTenders() });
      toast.success('Tender hidden');
    },
  });

  const unexcludeMutation = useMutation({
    mutationFn: apiUnexcludeTender,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crawlerKeys.excludedTenders() });
      toast.success('Tender restored');
    },
  });

  const keywordsSaveMutation = useMutation({
    mutationFn: saveKeywords,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crawlerKeys.keywords() });
      toast.success('Keywords saved');
      setEditedKeywords(null);
    },
    onError: (err) => toast.error('Failed to save keywords', { description: err.message, action: errorAction(err.message) }),
  });

  const [editedKeywords, setEditedKeywords] = useState<KeywordCategories | null>(null);

  const crawlers = crawlersQuery.data ?? [];
  const tenders = tendersQuery.data?.results ?? [];
  const keywords = editedKeywords ?? keywordsQuery.data ?? {};
  const hasKeywordChanges = editedKeywords !== null;
  const savedTenders = savedTendersQuery.data ?? [];
  const excludedKeys = useMemo(() => new Set(excludedKeysQuery.data ?? []), [excludedKeysQuery.data]);
  const [showExcluded, setShowExcluded] = useState(false);

  // Check if a tender is already saved (by solicitationNumber or source+name+url)
  const savedLookup = useMemo(() => {
    const byKey = new Set<string>();
    const byTenderId = new Set<string>();
    for (const s of savedTenders) {
      byKey.add(tenderKey(s.tender));
      if (s.tenderId) byTenderId.add(s.tenderId);
    }
    return { byKey, byTenderId };
  }, [savedTenders]);

  const isTenderSaved = (t: TenderResult) =>
    (t.solicitationNumber && savedLookup.byTenderId.has(t.solicitationNumber)) ||
    savedLookup.byKey.has(tenderKey(t));
  const isTenderExcluded = (t: TenderResult) => excludedKeys.has(tenderKey(t));

  // Poll job status
  useEffect(() => {
    if (!runState?.jobId) return;
    if (runState.status === 'completed' || runState.status === 'failed' || runState.status === 'cancelled') return;

    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${CRAWLERS_BASE}/jobs/${runState.jobId}`);
        if (!res.ok) return;
        const job: JobResponse = await res.json();

        const crawlerDetail = job.crawlers[runState.crawlerId];

        setRunState((prev) => prev ? {
          ...prev,
          status: job.status === 'completed' ? 'completed' : job.status === 'failed' ? 'failed' : job.status === 'cancelled' ? 'cancelled' : 'running',
          crawlerDetail,
          resultCount: job.resultCount,
          results: job.results,
        } : prev);

        if (job.status !== 'running') {
          if (pollRef.current) clearInterval(pollRef.current);
          // Refresh tenders list after job completes
          queryClient.invalidateQueries({ queryKey: crawlerKeys.tenderResults() });
          queryClient.invalidateQueries({ queryKey: crawlerKeys.tenders() });
        }
      } catch {
        // ignore transient errors
      }
    }, 1500);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [runState?.jobId, runState?.status, runState?.crawlerId, queryClient]);

  const handleRun = async (crawler: CrawlerInfo) => {
    setRunState({
      crawlerId: crawler.id,
      crawlerName: crawler.name,
      jobId: null,
      status: 'starting',
      resultCount: 0,
      results: [],
    });
    setSheetOpen(true);

    try {
      const res = await fetch(`${CRAWLERS_BASE}/crawlers/${crawler.id}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keywords: ['monitoring', 'calibration', 'datalogger', 'sensor'],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setRunState((prev) => prev ? { ...prev, jobId: data.jobId, status: 'running' } : prev);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to start crawler', { description: msg, action: errorAction(msg) });
      setRunState((prev) => prev ? { ...prev, status: 'failed' } : prev);
    }
  };

  const handleRunAll = async () => {
    const available = crawlers.filter((c) => c.status === 'idle');
    if (available.length === 0) {
      toast.error('All crawlers are already running');
      return;
    }

    setRunState({
      crawlerId: 'all',
      crawlerName: `All Tenders (${available.length})`,
      jobId: null,
      status: 'starting',
      resultCount: 0,
      results: [],
    });
    setSheetOpen(true);

    try {
      const res = await fetch(`${CRAWLERS_BASE}/crawlers/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: 'tenders',
          config: {
            keywords: ['monitoring', 'calibration', 'datalogger', 'sensor'],
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setRunState((prev) => prev ? { ...prev, jobId: data.jobId, status: 'running' } : prev);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to start crawlers', { description: msg, action: errorAction(msg) });
      setRunState((prev) => prev ? { ...prev, status: 'failed' } : prev);
    }
  };

  const handleCancel = async () => {
    if (!runState?.jobId) return;
    try {
      await fetch(`${CRAWLERS_BASE}/jobs/${runState.jobId}/cancel`, { method: 'POST' });
    } catch {
      // ignore
    }
  };

  // Source stats from tenders response (used for crawler sort + display)
  const sourceStats = tendersQuery.data?.sources;
  const activeSources = sourceStats ? Object.values(sourceStats).filter((s) => s.resultCount > 0).length : 0;

  // Tenders visible in the main Tenders tab — exclude saved + dismissed (unless showExcluded is on)
  const visibleTenders = useMemo(() => {
    if (showExcluded) return tenders;
    return tenders.filter((t) => !isTenderExcluded(t) && !isTenderSaved(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenders, showExcluded, excludedKeys, savedLookup]);

  // Rejected = manually excluded tenders
  const rejectedTenders = useMemo(
    () => tenders.filter((t) => isTenderExcluded(t)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenders, excludedKeys],
  );

  // Filtered + sorted crawlers
  const filteredCrawlers = useMemo(() => {
    const q = crawlerSearch.toLowerCase();
    const filtered = q
      ? crawlers.filter((c) => [c.name, c.description, c.id].join(' ').toLowerCase().includes(q))
      : [...crawlers];
    const dir = crawlerSortDesc ? -1 : 1;
    filtered.sort((a, b) => {
      const countA = sourceStats?.[a.id]?.resultCount ?? -1;
      const countB = sourceStats?.[b.id]?.resultCount ?? -1;
      return (countA - countB) * dir;
    });
    return filtered;
  }, [crawlers, crawlerSearch, crawlerSortDesc, sourceStats]);

  const isRunning = runState !== null && runState.status !== 'completed' && runState.status !== 'failed' && runState.status !== 'cancelled';

  // Derive step statuses
  const crawlStatus: StepStatus =
    !runState || runState.status === 'starting' ? 'pending' :
    runState.crawlerDetail?.status === 'completed' ? 'success' :
    runState.crawlerDetail?.status === 'failed' ? 'failed' :
    'running';

  const resultsStatus: StepStatus =
    runState?.status === 'completed' ? 'success' :
    runState?.status === 'failed' && crawlStatus === 'success' ? 'failed' :
    crawlStatus === 'success' ? 'running' :
    'pending';

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Radio className="h-5 w-5" />
            <h1 className="text-xl font-semibold">Tenders</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {tendersQuery.data
                ? <>{tendersQuery.data.totalResults} results from {activeSources} sources</>
                : 'Loading...'}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={isRunning}
              onClick={handleRunAll}
            >
              <Play className="h-3 w-3" /> Run All
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="tenders">Tenders {tendersQuery.data ? `(${tendersQuery.data.totalResults})` : ''}</TabsTrigger>
            <TabsTrigger value="saved">
              Saved {savedTenders.length > 0 && `(${savedTenders.length})`}
            </TabsTrigger>
            <TabsTrigger value="rejected">
              Rejected {rejectedTenders.length > 0 && `(${rejectedTenders.length})`}
            </TabsTrigger>
            <TabsTrigger value="crawlers">Crawlers {crawlersQuery.data ? `(${crawlersQuery.data.length})` : ''}</TabsTrigger>
            <TabsTrigger value="keywords">Keywords</TabsTrigger>
          </TabsList>

          {/* ── Tenders Tab ── */}
          <TabsContent value="tenders" className="mt-4">
            <TendersTable
              tenders={visibleTenders}
              isLoading={tendersQuery.isLoading}
              isError={tendersQuery.isError}
              isRefreshing={tendersQuery.isFetching}
              onRefresh={() => {
                queryClient.invalidateQueries({ queryKey: crawlerKeys.tenderResults() });
                queryClient.invalidateQueries({ queryKey: crawlerKeys.excludedTenders() });
                queryClient.invalidateQueries({ queryKey: crawlerKeys.savedTenders() });
              }}
              onRowClick={setSelectedTender}
              onToggleBookmark={(t) => {
                if (!isTenderSaved(t)) saveTenderMutation.mutate({ tender: t });
              }}
              onExclude={(t) => excludeMutation.mutate(tenderKey(t))}
              onRestore={(t) => unexcludeMutation.mutate(tenderKey(t))}
              isTenderSaved={isTenderSaved}
              isTenderExcluded={isTenderExcluded}
              excludedCount={excludedKeys.size}
              showExcluded={showExcluded}
              onToggleShowExcluded={() => setShowExcluded((v) => !v)}
            />
          </TabsContent>

          {/* ── Saved Tab ── */}
          <TabsContent value="saved" className="mt-4">
            <SavedTendersTable
              items={savedTenders}
              isLoading={savedTendersQuery.isLoading}
              isRefreshing={savedTendersQuery.isFetching}
              onRefresh={() => queryClient.invalidateQueries({ queryKey: crawlerKeys.savedTenders() })}
              onRowClick={(s) => setSelectedTender(s.tender)}
              onEditNotes={(s) => {
                const notes = window.prompt('Notes:', s.notes || '');
                if (notes !== null) updateNotesMutation.mutate({ id: s.id, notes });
              }}
              onConvert={(s) => convertMutation.mutate(s.id)}
              onDelete={(s) => deleteSavedMutation.mutate(s.id)}
              convertedTenders={convertedTenders}
              isConvertPending={convertMutation.isPending}
            />
          </TabsContent>

          {/* ── Rejected Tab ── */}
          <TabsContent value="rejected" className="mt-4">
            <RejectedTendersTable
              items={rejectedTenders}
              isRefreshing={tendersQuery.isFetching || excludedKeysQuery.isFetching}
              onRefresh={() => {
                queryClient.invalidateQueries({ queryKey: crawlerKeys.tenderResults() });
                queryClient.invalidateQueries({ queryKey: crawlerKeys.excludedTenders() });
              }}
              onRowClick={setSelectedTender}
              onRestore={(t) => unexcludeMutation.mutate(tenderKey(t))}
            />
          </TabsContent>

          {/* ── Crawlers Tab ── */}
          <TabsContent value="crawlers" className="mt-4">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="p-1" colSpan={3}>
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                        <Input className="h-7 text-xs pl-7" placeholder="Search crawlers..." value={crawlerSearch} onChange={(e) => setCrawlerSearch(e.target.value)} />
                      </div>
                    </TableHead>
                    <TableHead className="p-1" />
                    <TableHead className="p-1" />
                    <TableHead className="p-1" />
                    <TableHead className="p-1" />
                    <TableHead className="p-1 text-right">
                      <span className="text-xs text-muted-foreground font-normal">{filteredCrawlers.length}/{crawlers.length}</span>
                    </TableHead>
                  </TableRow>
                  <TableRow>
                    <TableHead className="w-[100px]">Screenshot</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="min-w-[200px]">Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => setCrawlerSortDesc((d) => !d)}>
                      <span className="inline-flex items-center">
                        Last Run
                        {crawlerSortDesc ? <ArrowDown className="h-3 w-3 ml-1" /> : <ArrowUp className="h-3 w-3 ml-1" />}
                      </span>
                    </TableHead>
                    <TableHead className="text-center">Success</TableHead>
                    <TableHead className="text-right">Site</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {crawlersQuery.isLoading ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading crawlers...</TableCell></TableRow>
                  ) : crawlersQuery.isError ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-red-600 py-8">Failed to load crawlers. Is the crawlers service running on port 3100?</TableCell></TableRow>
                  ) : filteredCrawlers.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No crawlers match the filters</TableCell></TableRow>
                  ) : (
                    filteredCrawlers.map((c) => {
                      const tenderCount = sourceStats?.[c.id]?.resultCount ?? c.lastRun?.resultCount ?? 0;
                      return (
                        <TableRow key={c.id}>
                          <TableCell className="w-[100px] py-2">
                            {c.lastRun ? (
                              <CrawlerScreenshotThumb
                                crawlerId={c.id}
                                onClick={() => setScreenshotCrawler(c)}
                              />
                            ) : (
                              <div className="w-[80px] h-[50px] rounded border bg-muted flex items-center justify-center">
                                <Image className="h-4 w-4 text-muted-foreground/20" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-medium whitespace-nowrap">{c.name}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{c.description}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={cn(
                              c.status === 'running' ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' :
                              c.lastRun ? 'bg-green-100 text-green-800 hover:bg-green-100' :
                              'bg-gray-100 text-gray-800 hover:bg-gray-100',
                            )}>
                              {c.status === 'running' ? 'Running' : c.lastRun ? 'Ready' : 'Idle'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {c.lastRun ? (
                              <div
                                className="cursor-pointer hover:text-foreground transition-colors"
                                onClick={() => {
                                  try {
                                    const raw = localStorage.getItem('atek-tenders-column-filters');
                                    const prev = raw ? JSON.parse(raw) : {};
                                    localStorage.setItem('atek-tenders-column-filters', JSON.stringify({
                                      ...prev,
                                      source: JSON.stringify([c.id]),
                                    }));
                                  } catch { /* ignore */ }
                                  setTab('tenders');
                                }}
                                title="View results in Tenders tab"
                              >
                                <span className="underline decoration-dotted">{tenderCount} results</span> &middot; {(c.lastRun.durationMs / 1000).toFixed(1)}s
                                <p className="text-xs text-muted-foreground/70">{new Date(c.lastRun.completedAt).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' })}</p>
                              </div>
                            ) : <span className="text-muted-foreground/50">Never</span>}
                          </TableCell>
                          <TableCell className="text-center">
                            {c.stats ? (
                              <div className="text-xs" title={`${c.stats.successfulRuns}/${c.stats.totalRuns} runs · avg ${(c.stats.avgDurationMs / 1000).toFixed(1)}s · avg ${c.stats.avgResultsPerRun} results/run`}>
                                <span className={cn(
                                  'font-semibold tabular-nums',
                                  c.stats.successRate >= 90 ? 'text-green-700' :
                                  c.stats.successRate >= 70 ? 'text-yellow-700' :
                                  'text-red-700',
                                )}>
                                  {c.stats.successRate}%
                                </span>
                                <p className="text-muted-foreground/70 text-[10px]">{c.stats.totalRuns} runs</p>
                              </div>
                            ) : (
                              <span className="text-muted-foreground/30 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <a href={c.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" className="h-7 px-2" disabled={isRunning || c.status === 'running'} onClick={() => handleRun(c)}>
                              {(isRunning && runState?.crawlerId === c.id) || c.status === 'running'
                                ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ── Keywords Tab ── */}
          <TabsContent value="keywords" className="mt-4">
            <KeywordsTable
              categories={keywords}
              isLoading={keywordsQuery.isLoading}
              isError={keywordsQuery.isError}
              isRefreshing={keywordsQuery.isFetching}
              hasChanges={hasKeywordChanges}
              isSaving={keywordsSaveMutation.isPending}
              onRefresh={() => queryClient.invalidateQueries({ queryKey: crawlerKeys.keywords() })}
              onChange={setEditedKeywords}
              onSave={() => editedKeywords && keywordsSaveMutation.mutate(editedKeywords)}
              onDiscard={() => setEditedKeywords(null)}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Tender detail panel */}
      <Sheet open={!!selectedTender} onOpenChange={(open) => { if (!open) setSelectedTender(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
          {selectedTender && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base leading-snug pr-4">{selectedTender.name}</SheetTitle>
                {selectedTender.organisationName && (
                  <p className="text-sm text-muted-foreground">{selectedTender.organisationName}</p>
                )}
              </SheetHeader>

              <div className="flex-1 mt-6 space-y-5 overflow-y-auto">
                {/* Score + meta */}
                <div className="flex flex-wrap items-center gap-3">
                  {(selectedTender.confidenceScore != null || selectedTender.relevanceScore != null) && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">Score:</span>
                      <ScoreBadge score={selectedTender.confidenceScore ?? selectedTender.relevanceScore ?? 0} />
                    </div>
                  )}
                  <Badge variant="outline" className="text-xs">{selectedTender.source}</Badge>
                  {selectedTender.language && (
                    <Badge variant="outline" className="text-xs uppercase">{selectedTender.language}</Badge>
                  )}
                  {selectedTender.matchedKeyword && (
                    <Badge variant="secondary" className="text-xs">{selectedTender.matchedKeyword}</Badge>
                  )}
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selectedTender.publishDate && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Published</span>
                      <span>{selectedTender.publishDate}</span>
                    </div>
                  )}
                  {selectedTender.deadline && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Deadline</span>
                      <span>{selectedTender.deadline}</span>
                    </div>
                  )}
                  {selectedTender.solicitationNumber && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Solicitation #</span>
                      <span className="font-mono text-xs">{selectedTender.solicitationNumber}</span>
                    </div>
                  )}
                  {selectedTender.estimatedValue && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Est. Value</span>
                      <span>{selectedTender.estimatedValue}</span>
                    </div>
                  )}
                  {selectedTender.category && (
                    <div className="col-span-2">
                      <span className="text-xs text-muted-foreground block">Category</span>
                      <span>{selectedTender.category}</span>
                    </div>
                  )}
                </div>

                {/* AI Summary */}
                {selectedTender.summary && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">AI Summary</span>
                    <p className="text-sm">{selectedTender.summary}</p>
                  </div>
                )}

                {/* AI Relevance reason */}
                {selectedTender.relevanceReason && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Relevance Reason</span>
                    <p className="text-sm">{selectedTender.relevanceReason}</p>
                  </div>
                )}

                {/* Confidence breakdown */}
                {selectedTender.confidenceBreakdown && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-2">Confidence Breakdown</span>
                    <div className="grid grid-cols-2 gap-2">
                      {([
                        ['ICP Fit', selectedTender.confidenceBreakdown.icp],
                        ['Product', selectedTender.confidenceBreakdown.product],
                        ['Compliance', selectedTender.confidenceBreakdown.compliance],
                        ['Opportunity', selectedTender.confidenceBreakdown.opportunity],
                      ] as const).map(([label, value]) => (
                        <div key={label} className="flex items-center justify-between rounded border px-2 py-1.5">
                          <span className="text-xs text-muted-foreground">{label}</span>
                          <span className={cn('text-xs font-semibold tabular-nums',
                            value >= 70 ? 'text-green-700' : value >= 40 ? 'text-yellow-700' : 'text-gray-500',
                          )}>{value}</span>
                        </div>
                      ))}
                      {selectedTender.confidenceBreakdown.antiPatternPenalty > 0 && (
                        <div className="col-span-2 flex items-center justify-between rounded border border-red-200 bg-red-50 px-2 py-1.5">
                          <span className="text-xs text-red-600">Anti-pattern Penalty</span>
                          <span className="text-xs font-semibold text-red-700">-{selectedTender.confidenceBreakdown.antiPatternPenalty}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Description</span>
                  <p className="text-sm whitespace-pre-wrap">{selectedTender.description}</p>
                </div>

                {/* Screenshot */}
                {selectedTender.screenshotUrl && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Screenshot</span>
                    <img
                      src={`${CRAWLERS_BASE}${selectedTender.screenshotUrl}`}
                      alt="Tender page"
                      className="rounded border w-full"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t flex gap-2">
                <Button asChild variant="default" size="sm" className="gap-1">
                  <a href={selectedTender.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3 w-3" /> Open Original
                  </a>
                </Button>
                {isTenderSaved(selectedTender) ? (
                  <Button variant="outline" size="sm" className="gap-1 text-yellow-600" disabled>
                    <BookmarkCheck className="h-3 w-3" /> Saved
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    disabled={saveTenderMutation.isPending}
                    onClick={() => saveTenderMutation.mutate({ tender: selectedTender })}
                  >
                    {saveTenderMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bookmark className="h-3 w-3" />}
                    Save
                  </Button>
                )}
                {(() => {
                  const savedMatch = savedTenders.find((s) => s.tenderId === selectedTender.solicitationNumber || tenderKey(s.tender) === tenderKey(selectedTender));
                  const converted = savedMatch && convertedTenders.has(savedMatch.id);
                  if (converted) {
                    const oppId = convertedTenders.get(savedMatch!.id)?.opportunityId;
                    return (
                      <Button asChild variant="outline" size="sm" className="gap-1 border-orange-300 text-orange-600 hover:bg-orange-50">
                        <a href={`/crm/opportunities/${oppId}`} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3 w-3" /> Voir dans opportunité
                        </a>
                      </Button>
                    );
                  }
                  return (
                    <Button
                      variant="default"
                      size="sm"
                      className="gap-1 bg-green-600 hover:bg-green-700"
                      disabled={convertMutation.isPending}
                      onClick={() => {
                        if (savedMatch) {
                          convertMutation.mutate(savedMatch.id);
                        } else {
                          saveTenderMutation.mutate({ tender: selectedTender }, {
                            onSuccess: (data: any) => {
                              if (data?.id) convertMutation.mutate(data.id);
                            },
                          });
                        }
                      }}
                    >
                      {convertMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightCircle className="h-3 w-3" />}
                      Move to Opportunity
                    </Button>
                  );
                })()}
                <Button variant="outline" size="sm" className="ml-auto" onClick={() => setSelectedTender(null)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Crawler screenshots sheet */}
      <Sheet open={!!screenshotCrawler} onOpenChange={(open) => { if (!open) setScreenshotCrawler(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
          {screenshotCrawler && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base">{screenshotCrawler.name}</SheetTitle>
                <p className="text-sm text-muted-foreground">{screenshotCrawler.description}</p>
              </SheetHeader>

              <div className="flex-1 mt-4 space-y-4 overflow-y-auto">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2">
                  {screenshotCrawler.lastRun && (
                    <>
                      <div className="rounded border px-3 py-2 text-center">
                        <p className="text-lg font-semibold tabular-nums">{sourceStats?.[screenshotCrawler.id]?.resultCount ?? screenshotCrawler.lastRun.resultCount}</p>
                        <p className="text-[10px] text-muted-foreground">Results</p>
                      </div>
                      <div className="rounded border px-3 py-2 text-center">
                        <p className="text-lg font-semibold tabular-nums">{(screenshotCrawler.lastRun.durationMs / 1000).toFixed(1)}s</p>
                        <p className="text-[10px] text-muted-foreground">Duration</p>
                      </div>
                    </>
                  )}
                  {screenshotCrawler.stats && (
                    <div className="rounded border px-3 py-2 text-center">
                      <p className={cn('text-lg font-semibold tabular-nums',
                        screenshotCrawler.stats.successRate >= 90 ? 'text-green-700' :
                        screenshotCrawler.stats.successRate >= 70 ? 'text-yellow-700' :
                        'text-red-700',
                      )}>{screenshotCrawler.stats.successRate}%</p>
                      <p className="text-[10px] text-muted-foreground">{screenshotCrawler.stats.totalRuns} runs</p>
                    </div>
                  )}
                </div>

                {/* Screenshots */}
                <div>
                  <span className="text-xs text-muted-foreground block mb-2">Screenshots from last run</span>
                  {screenshotsQuery.isLoading ? (
                    <div className="flex items-center gap-2 py-4 text-muted-foreground text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading screenshots...
                    </div>
                  ) : !screenshotsQuery.data || screenshotsQuery.data.length === 0 ? (
                    <p className="text-sm text-muted-foreground/50 py-4">No screenshots available. Run this crawler first.</p>
                  ) : (
                    <div className="space-y-3">
                      {screenshotsQuery.data.map((ss) => (
                        <div key={ss.step} className="space-y-1">
                          <img
                            src={`${CRAWLERS_BASE}/crawlers/${screenshotCrawler.id}/screenshots/${ss.step}`}
                            alt={`Step ${ss.step}`}
                            className="rounded border w-full"
                            loading="lazy"
                          />
                          <p className="text-[11px] text-muted-foreground truncate">{ss.pageUrl}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t flex gap-2">
                <Button asChild variant="default" size="sm" className="gap-1">
                  <a href={screenshotCrawler.website} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3 w-3" /> Visit Site
                  </a>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  disabled={isRunning || screenshotCrawler.status === 'running'}
                  onClick={() => { setScreenshotCrawler(null); handleRun(screenshotCrawler); }}
                >
                  <Play className="h-3 w-3" /> Run
                </Button>
                <Button variant="outline" size="sm" className="ml-auto" onClick={() => setScreenshotCrawler(null)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Run progress sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              {runState?.crawlerName ?? 'Crawl'}
            </SheetTitle>
            <p className="text-sm text-muted-foreground">
              {runState?.status === 'starting' && 'Starting crawler...'}
              {runState?.status === 'running' && `Job: ${runState.jobId ?? '...'}`}
              {runState?.status === 'completed' && `Completed — ${runState.resultCount} result(s)`}
              {runState?.status === 'failed' && 'Run failed'}
              {runState?.status === 'cancelled' && 'Run cancelled'}
            </p>
          </SheetHeader>

          <div className="flex-1 mt-6 space-y-3 overflow-y-auto">
            <StepCard
              icon={Search}
              label="Crawl Source"
              description="Fetching procurement listings"
              status={crawlStatus}
              detail={crawlStatus === 'success' && runState?.crawlerDetail
                ? `${runState.crawlerDetail.resultCount} results in ${((runState.crawlerDetail.durationMs ?? 0) / 1000).toFixed(1)}s`
                : runState?.crawlerDetail?.error}
            />
            <StepCard
              icon={FileText}
              label="Results"
              description="Processing and scoring results"
              status={resultsStatus}
              detail={resultsStatus === 'success' ? `${runState?.resultCount ?? 0} total results` : undefined}
            />

            {/* Inline results in sheet */}
            {runState && runState.results.length > 0 && (
              <div className="space-y-2 pt-2">
                <h3 className="text-sm font-medium">Results ({runState.results.length})</h3>
                {runState.results.map((r, i) => (
                  <div key={`${r.source}-${i}`} className="rounded-lg border p-3 space-y-1">
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-600 hover:underline line-clamp-2">
                      {r.name}
                    </a>
                    {r.organisationName && <p className="text-xs text-muted-foreground">{r.organisationName}</p>}
                    {r.summary && <p className="text-xs text-muted-foreground italic">{r.summary}</p>}
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      {r.relevanceScore != null && <ScoreBadge score={r.relevanceScore} />}
                      {r.publishDate && <span>Published: {r.publishDate}</span>}
                      {r.deadline && <span>Deadline: {r.deadline}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t flex gap-2">
            {isRunning && (
              <Button variant="destructive" size="sm" className="gap-1" onClick={handleCancel}>
                <StopCircle className="h-3 w-3" /> Cancel
              </Button>
            )}
            {(runState?.status === 'completed' || runState?.status === 'failed' || runState?.status === 'cancelled') && (
              <Button variant="outline" className="w-full" onClick={() => setSheetOpen(false)}>
                Close
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
